import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MODE_DELIVERY } from "../../../../constants/constants";
import { useConfig } from "../../../../context/ConfigContext";
import EmptyCartIcon from "../../../../images/empty-cartIcon.svg";
import Bin from "../../../../images/red_bin.svg";
import { shopApi } from "../../../../services/apiGate";
import { updateCart } from "../../../../store/cart/cartActions";
import { RootState } from "../../../../store/rootState";
import { getIcon, IconType } from "../../../../theme/icons";
import { calculTotalCart } from "../../../../utils/priceUtils";
import CartHeader from "../../../shared/AppNavbar/CartModal/CartHeader/CartHeader";
import OrderDetails from "../../../shared/OrderDetails/OrderDetails";
import "./CartMenu.scss";

interface CartItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

interface CartMenuProps {
  setShowRestaurantError: (show: boolean) => void;
}

const CartMenu: React.FC<CartMenuProps> = ({ setShowRestaurantError }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const selectedRestaurant = useSelector(
    (state: any) => state.shop.selectedRestaurant,
  );
  const restaurantId = selectedRestaurant?.id;

  // Get ALL categories from Redux
  const restaurantCarts = useSelector(
    (state: RootState) => state.cart.restaurantCarts,
  );

  // Get the specific restaurant cart
  const restaurantCart = restaurantCarts[Number(restaurantId) || 0];

  // Fixed: Better dependencies for useMemo
  const totalAmount = useMemo(() => {
    return calculTotalCart(restaurantCart);
  }, [restaurantCart?.categories, restaurantCart?.mode]);

  const isCheckoutDisabled = useMemo(() => {
    if (!restaurantCart || restaurantCart.mode !== MODE_DELIVERY) return false;
    return (
      totalAmount === 0 ||
      totalAmount < (selectedRestaurant?.cartThreshold || 0)
    );
  }, [restaurantCart, totalAmount, selectedRestaurant]);

  const checkoutMessage = useMemo(() => {
    if (!restaurantCart || restaurantCart.mode !== MODE_DELIVERY) return "";
    if (totalAmount === 0) return "Votre panier est vide.";
    if (totalAmount < (selectedRestaurant?.cartThreshold || 0)) {
      return `Le montant minimum pour la livraison est de ${selectedRestaurant?.cartThreshold} €. Ajoutez plus d’articles pour continuer.`;
    }
    return "";
  }, [restaurantCart, totalAmount, selectedRestaurant]);

  const totalItems = restaurantCart?.categories?.length || 0;

  const toggleCart = () => {
    setIsOpen(!isOpen);
  };

  const closeCart = () => {
    setIsOpen(false);
  };

  const goToCart = () => {
    navigate("/cart");
    setIsOpen(false);
  };

  const checkRestaurantDisponibilty = async (): Promise<boolean> => {
    try {
      const response = await shopApi.getResturantById({
        id: Number(selectedRestaurant?.id),
        diningMode: Number(restaurantCart?.mode || 0),
        adresse: {
          latitude: 0,
          longitude: 0,
        },
      });
      const { restaurant } = response.data;
      return !!(restaurant?.isEnabled && restaurant?.isOpen);
    } catch {
      return false;
    }
  };

  const checkUserAuthentication = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      localStorage.setItem("redirectedToAuth", "true");
      navigate("/Authentification");
    } else {
      localStorage.setItem("redirectedToAuth", "false");
      navigate("/paiement");
    }
  };

  const goToCheckout = async () => {
    const disponible = await checkRestaurantDisponibilty();
    if (disponible) {
      localStorage.setItem("restaurantId", String(selectedRestaurant?.id));
      checkUserAuthentication();
    } else {
      setShowRestaurantError(true);
    }
    setIsOpen(false);
  };

  // Fixed: Now uses restaurantCarts instead of restaurantCart
  const handleCancel = () => {
    const updatedRestaurantCarts = { ...restaurantCarts };
    if (selectedRestaurant?.id) {
      delete updatedRestaurantCarts[Number(selectedRestaurant.id)];
    }
    dispatch(updateCart(updatedRestaurantCarts));
  };

  const goToRestaurant = () => {
    if (selectedRestaurant) navigate(`/restaurant/${selectedRestaurant.id}`);
  };

  return (
    <div className="cart-menu-container">
      {/* Cart Button */}
      <button className="auth-cart-btn" onClick={toggleCart}>
        {getIcon(IconType.PANIER, {
          color: config?.primaryColor,
          onClick: () => toggleCart(),
        })}
        {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="cart-dropdown">
            {/* Mobile Close Button */}
            <button className="mobile-close-btn" onClick={closeCart}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className="cart-dropdown-content">
              {/* Empty Cart */}
              {!restaurantCart?.categories?.length ? (
                <div className="panier-wrapper">
                  <img
                    className="empty-cart-icon"
                    src={EmptyCartIcon}
                    alt="EmptyCart"
                  />
                  <p className="empty-cart-title">Aucun Article ajouté</p>
                  <span className="empty-cart-subtitle">
                    Ajoutez des articles, ils seront affichés ici.
                  </span>
                </div>
              ) : (
                <>
                  {/* Cart Header */}
                  <div className="cart-components">
                    <CartHeader
                      restaurantCart={restaurantCart}
                      restaurant={selectedRestaurant}
                    />
                  </div>

                  <div className="cart-dropdown-divider" />

                  <OrderDetails
                    categories={restaurantCart?.categories || []}
                    orderType={restaurantCart?.mode || 0}
                    canApplyArticleDiscount={false}
                    canEditQuantity={true}
                    restaurantId={selectedRestaurant?.id || ""}
                  />

                  <div className="cancel-btn-wrapper">
                    <button className="cancel-btn" onClick={handleCancel}>
                      <img src={Bin} alt="" />
                      Annuler commande
                    </button>
                  </div>

                  {/* Footer Buttons */}
                  <div className="cart-footer">
                    <button
                      className={`btn-command ${isCheckoutDisabled ? "btn-disabled" : ""}`}
                      disabled={isCheckoutDisabled}
                      onClick={() => {
                        localStorage.setItem(
                          "restaurantId",
                          String(selectedRestaurant?.id),
                        );
                        goToCheckout();
                      }}
                    >
                      Commander • {totalAmount}€
                    </button>
                    {isCheckoutDisabled && checkoutMessage && (
                      <div
                        style={{
                          backgroundColor: "#fff3cd",
                          color: "#856404",
                          padding: "10px 15px",
                          borderRadius: "8px",
                          border: "1px solid #ffeeba",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "14px",
                        }}
                      >
                        <span style={{ marginRight: "8px" }}>⚠️</span>
                        {checkoutMessage}
                      </div>
                    )}

                    <button
                      onClick={goToRestaurant}
                      className="btn-add-articles"
                    >
                      Ajouter article(s)
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Overlay to close dropdown */}
          <div className="cart-dropdown-overlay" onClick={closeCart} />
        </>
      )}
    </div>
  );
};

export default CartMenu;
